import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useSearchParams } from 'react-router'

import { Spinner } from '@/components/spinner'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import useTranslator from '@/hooks/translation'

import { useFormSteps } from '../../hooks/use-form-steps'
import { LoginSchema, loginSchema } from './schema'

export function LoginForm() {
  const [searchParams] = useSearchParams()
  const { getTranslation } = useTranslator()
  const [key, setKey] = useState<number>(0)

  const emailValue = searchParams.get('email') ?? ''

  useEffect(() => {
    setKey((prev) => prev + 1)
  }, [emailValue])

  const form = useForm<LoginSchema>({
    mode: 'onSubmit',
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: emailValue,
      password: '',
    },
  })

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = form

  const {
    isLoading: isAuthenticating,
    currentStep,
    goToNextStep,
    goToPreviousStep,
  } = useFormSteps(form)

  const isLoading = isSubmitting || isAuthenticating

  async function onAdvance() {
    if (isLoading) return

    await goToNextStep()
  }

  function getButtonLoginText() {
    return currentStep === 1
      ? getTranslation('auth.login.continue')
      : getTranslation('auth.login.continueWithEmail')
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault()
      onAdvance()
    }
  }

  return (
    <Form {...form} key={key}>
      <form
        onSubmit={handleSubmit(onAdvance)}
        className="mx-auto flex w-full min-w-[200px] flex-col justify-between md:mx-0"
        onKeyDown={handleKeyDown}
      >
        <div className="space-y-6 md:px-8">
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{getTranslation('common.yourEmail')}</FormLabel>
                <div className="space-y-1">
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="name@company.com"
                      {...field}
                    />
                  </FormControl>
                  {errors.email?.message && <FormMessage />}
                </div>
              </FormItem>
            )}
          />

          {currentStep === 2 && (
            <FormField
              control={control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <div className="flex justify-between">
                    <FormLabel>
                      {getTranslation('common.yourPassword')}
                    </FormLabel>
                    <Button
                      variant="link"
                      size="fit"
                      disabled={isSubmitting}
                      asChild
                      className="text-xs md:text-sm"
                    >
                      <Link to="/forgot-password">
                        {getTranslation('auth.resetPassword.question')}
                      </Link>
                    </Button>
                  </div>
                  <div className="space-y-1">
                    <FormControl>
                      <Input
                        type="password"
                        placeholder={getTranslation(
                          'common.validations.passwordRequired',
                        )}
                        {...field}
                      />
                    </FormControl>
                    {errors.password?.message && <FormMessage />}
                  </div>
                </FormItem>
              )}
            />
          )}
        </div>

        <div className="mb-16 mt-10 flex flex-col space-y-4 md:rounded-b-3xl md:px-8">
          <Button
            type="button"
            variant="fintalk"
            disabled={isLoading}
            onClick={onAdvance}
          >
            {isLoading && <Spinner className="text-fintalk-foreground" />}
            {getButtonLoginText()}
          </Button>
        </div>

        {currentStep === 2 && (
          <div className="flex w-full items-center justify-between bg-background/80 p-6 md:rounded-b-2xl md:border-t">
            <Button
              type="button"
              variant="link"
              disabled={isLoading}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                goToPreviousStep()
              }}
            >
              {getTranslation('common.back')}
            </Button>
          </div>
        )}
      </form>
    </Form>
  )
}
